// @ts-nocheck
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { usersApi } from '@/services/api';
import { User } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { ShieldAlert, Mail, Clock, Calendar, KeyRound, UserCog, Users, UserPlus, AlertCircle, Send, X, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

// Maximum number of users allowed in the system
const MAX_USERS = 25;

// Build a display name from first/last name, falling back to email
const userDisplayName = (u: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;

const UsersPage = () => {
  const [error, setError] = useState<string>('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);

  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const { data: response, isLoading, error: queryError } = useQuery(
    ['users', currentPage, searchTerm, roleFilter],
    () => usersApi.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm || undefined,
      role: roleFilter || undefined,
    }),
    {
      onError: (error) => {
        setError('Failed to load users');
        console.error('Users query error:', error);
      },
      enabled: currentUser?.role === 'admin' || currentUser?.role === 'super_admin',
    }
  );

  // Separate query to get total user count (unfiltered) for limit checking
  const { data: totalUsersResponse } = useQuery(
    ['users', 'total-count'],
    () => usersApi.getAll({ page: 1, limit: 1 }),
    {
      enabled: currentUser?.role === 'admin' || currentUser?.role === 'super_admin',
    }
  );

  const totalUserCount = totalUsersResponse?.pagination?.total || 0;
  const isUserLimitReached = totalUserCount >= MAX_USERS;

  // Query for pending invitations
  const { data: invitationsResponse, isLoading: invitationsLoading } = useQuery(
    ['invitations'],
    () => usersApi.getInvitations(),
    {
      enabled: currentUser?.role === 'admin' || currentUser?.role === 'super_admin',
    }
  );

  const pendingInvitations = invitationsResponse?.data || [];

  const inviteMutation = useMutation(usersApi.inviteUser, {
    onSuccess: () => {
      setShowInviteModal(false);
      queryClient.invalidateQueries('users');
      queryClient.invalidateQueries('invitations');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to invite user');
    },
  });

  const cancelInvitationMutation = useMutation(usersApi.cancelInvitation, {
    onSuccess: () => {
      queryClient.invalidateQueries('invitations');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to cancel invitation');
    },
  });

  const resendInvitationMutation = useMutation(usersApi.resendInvitation, {
    onSuccess: () => {
      queryClient.invalidateQueries('invitations');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to resend invitation');
    },
  });

  const createMutation = useMutation(usersApi.create, {
    onSuccess: () => {
      setShowCreateModal(false);
      queryClient.invalidateQueries('users');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to create user');
    },
  });

  const deleteMutation = useMutation(usersApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete user');
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: { email?: string; role?: string; firstName?: string; lastName?: string } }) =>
      usersApi.update(id, data),
    {
      onSuccess: () => {
        setShowEditModal(false);
        setEditingUser(null);
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        setError(error.response?.data?.error || 'Failed to update user');
      },
    }
  );

  const changePasswordMutation = useMutation(
    ({ id, password }: { id: string; password: string }) =>
      usersApi.changePassword(id, password),
    {
      onSuccess: () => {
        setShowPasswordModal(false);
        setEditingUser(null);
        queryClient.invalidateQueries('users');
      },
      onError: (error: any) => {
        setError(error.response?.data?.error || 'Failed to change password');
      },
    }
  );

  const users = response?.data || [];
  const pagination = response?.pagination;

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleChangePassword = (user: User) => {
    setEditingUser(user);
    setShowPasswordModal(true);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-50 text-purple-700 border border-purple-200';
      case 'admin':
        return 'bg-[#E8F4FD] text-[#0B7BC7] border border-[#B3DCFA]';
      default:
        return 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]';
    }
  };

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super_admin')) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-50 flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-[#EF4444]" />
        </div>
        <div className="text-[#EF4444] text-lg font-medium mb-2">
          Access Denied
        </div>
        <p className="text-[#6B7280] text-[15px]">Admin privileges required to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-end">
          <div>
            <div className="h-9 w-56 md-skeleton mb-2"></div>
            <div className="h-5 w-32 md-skeleton"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-11 w-32 md-skeleton rounded-[10px]"></div>
            <div className="h-11 w-32 md-skeleton rounded-[10px]"></div>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 h-11 md-skeleton rounded-[10px]"></div>
          <div className="h-11 w-48 md-skeleton rounded-[10px]"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-[14px] border border-[#ECEEF1] p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-6 md-skeleton w-3/4 mb-2"></div>
                  <div className="h-4 md-skeleton w-1/3"></div>
                </div>
                <div className="h-6 w-16 md-skeleton rounded-full"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 md-skeleton w-full"></div>
                <div className="h-4 md-skeleton w-5/6"></div>
              </div>
              <div className="flex gap-2 pt-4">
                <div className="h-10 md-skeleton flex-1 rounded-[10px]"></div>
                <div className="h-10 md-skeleton flex-1 rounded-[10px]"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-end animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">User Management</h1>
          <p className="text-[#6B7280] text-[15px]">
            {totalUserCount} {totalUserCount === 1 ? 'user' : 'users'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowInviteModal(true)}
            className="md-btn md-btn-outlined disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUserLimitReached}
            title={isUserLimitReached ? `Maximum of ${MAX_USERS} users reached` : 'Invite a new user via email'}
          >
            <Mail className="w-4 h-4" />
            Invite User
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="md-btn md-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUserLimitReached}
            title={isUserLimitReached ? `Maximum of ${MAX_USERS} users reached` : 'Create a new user'}
          >
            <UserPlus className="w-4 h-4" />
            Create User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in-up stagger-1">
        <div className="md:col-span-3">
          <div className="md-outlined-textfield">
            <input
              type="text"
              placeholder="Search users by email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full"
            />
          </div>
        </div>
        <div className="md-outlined-textfield">
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full"
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
            {currentUser.role === 'super_admin' && (
              <option value="super_admin">Super Admin</option>
            )}
          </select>
        </div>
      </div>

      {/* Error Display */}
      {(error || queryError) && (
        <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl animate-fade-in-up">
          {error || 'Failed to load users. Please try again.'}
        </div>
      )}

      {/* Pending Invitations Section */}
      {pendingInvitations.length > 0 && (
        <div className="animate-fade-in-up stagger-1">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-5 h-5 text-[#F59E0B]" />
            <h2 className="text-lg font-bold font-heading text-[#1A1F2B]">
              Pending Invitations ({pendingInvitations.length})
            </h2>
          </div>
          <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-4 mb-8">
            <div className="space-y-3">
              {pendingInvitations.map((invitation: any) => (
                <div
                  key={invitation.id}
                  className={`flex items-center justify-between p-4 bg-white rounded-lg border ${
                    invitation.isExpired ? 'border-red-200' : 'border-[#ECEEF1]'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-[#1A1F2B] truncate">
                        {invitation.email}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getRoleBadge(invitation.role)}`}>
                        {invitation.role.replace('_', ' ')}
                      </span>
                      {invitation.isExpired && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                          Expired
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-[#6B7280]">
                      Invited by {invitation.invitedByEmail} on {formatDate(invitation.createdAt)}
                      {!invitation.isExpired && (
                        <span className="text-[#9CA3AF]">
                          {' '}• Expires {formatDate(invitation.expiresAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => resendInvitationMutation.mutate(invitation.id)}
                      disabled={resendInvitationMutation.isLoading}
                      className="md-btn md-btn-outlined text-[13px] px-3 py-1.5"
                      title="Resend invitation email"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${resendInvitationMutation.isLoading ? 'animate-spin' : ''}`} />
                      Resend
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Cancel invitation for "${invitation.email}"?`)) {
                          cancelInvitationMutation.mutate(invitation.id);
                        }
                      }}
                      disabled={cancelInvitationMutation.isLoading}
                      className="md-btn md-btn-danger text-[13px] px-3 py-1.5"
                      title="Cancel invitation"
                    >
                      <X className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Users Cards */}
      {users.length === 0 ? (
        <div className="text-center py-20 animate-fade-in-up stagger-2">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#E8F4FD] flex items-center justify-center">
            <Users className="w-8 h-8 text-[#18A5F9]" />
          </div>
          <p className="text-lg text-[#6B7280] mb-6">
            {searchTerm || roleFilter 
              ? 'No users found matching your filters.'
              : 'No users found. Click "Create User" to add your first user.'}
          </p>
          {!searchTerm && !roleFilter && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="md-btn md-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isUserLimitReached}
              title={isUserLimitReached ? `Maximum of ${MAX_USERS} users reached` : 'Create a new user'}
            >
              Create First User
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {users.map((user: User, index: number) => (
            <div key={user.id} className={`md-card p-6 animate-fade-in-up stagger-${Math.min(index + 1, 6)}`}>
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold font-heading text-[#1A1F2B] mb-1 truncate">
                    {userDisplayName(user)}
                  </h3>
                  {(user.firstName || user.lastName) && (
                    <p className="text-[13px] text-[#6B7280] truncate">{user.email}</p>
                  )}
                  <p className="text-[13px] text-[#9CA3AF] font-mono">
                    ID: {user.id.slice(-8)}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${getRoleBadge(user.role)}`}>
                  {user.role.replace('_', ' ')}
                </span>
              </div>

              {/* Card Content */}
              <div className="space-y-2.5 mb-5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#9CA3AF] flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Last Login
                  </span>
                  <span className="font-semibold text-[#374151]">
                    {user.lastLogin
                      ? formatDate(user.lastLogin)
                      : 'Never'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[#9CA3AF] flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    Created
                  </span>
                  <span className="font-semibold text-[#374151]">
                    {formatDate(user.createdAt)}
                  </span>
                </div>
              </div>

              {/* Card Actions */}
              <div className="flex gap-2 pt-4 border-t border-[#ECEEF1]">
                <button
                  onClick={() => handleEditUser(user)}
                  className="md-btn md-btn-outlined flex-1 text-[13px]"
                >
                  <UserCog className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => handleChangePassword(user)}
                  className="md-btn md-btn-primary flex-1 text-[13px]"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  Password
                </button>
                {user.id !== currentUser.id &&
                 currentUser.role !== 'admin' && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete "${user.email}"?`)) {
                        deleteMutation.mutate(user.id);
                      }
                    }}
                    className="md-btn md-btn-danger text-[13px] px-4"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-8 animate-fade-in-up stagger-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="md-btn md-btn-outlined px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed text-[13px]"
          >
            Previous
          </button>
          <span className="text-sm text-[#6B7280] font-medium">
            Page {currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={currentPage === pagination.totalPages}
            className="md-btn md-btn-outlined px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed text-[13px]"
          >
            Next
          </button>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvite={(email, role) => inviteMutation.mutate({ email, role })}
          isLoading={inviteMutation.isLoading}
          currentUserRole={currentUser.role}
        />
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(email, password, role, firstName, lastName) => createMutation.mutate({ email, password, role, firstName, lastName })}
          isLoading={createMutation.isLoading}
          currentUserRole={currentUser.role}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onUpdate={(id, email, role, firstName, lastName) => updateMutation.mutate({ id, data: { email, role, firstName, lastName } })}
          isLoading={updateMutation.isLoading}
          currentUserRole={currentUser.role}
        />
      )}

      {/* Change Password Modal */}
      {showPasswordModal && editingUser && (
        <ChangePasswordModal
          user={editingUser}
          onClose={() => {
            setShowPasswordModal(false);
            setEditingUser(null);
          }}
          onPasswordChange={(id, password) => changePasswordMutation.mutate({ id, password })}
          isLoading={changePasswordMutation.isLoading}
        />
      )}
    </div>
  );
};

// Invite User Modal Component
const InviteUserModal = ({
  onClose,
  onInvite,
  isLoading,
  currentUserRole
}: {
  onClose: () => void;
  onInvite: (email: string, role: string) => void;
  isLoading: boolean;
  currentUserRole: string;
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onInvite(email, role);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Invite User</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Email</label>
            <div className="md-outlined-textfield">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                placeholder="user@example.com"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Role</label>
            <div className="md-outlined-textfield">
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full">
                <option value="user">User</option>
                {currentUserRole === 'super_admin' && (
                  <option value="admin">Admin</option>
                )}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Inviting...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create User Modal Component
const CreateUserModal = ({
  onClose,
  onCreate,
  isLoading,
  currentUserRole
}: {
  onClose: () => void;
  onCreate: (email: string, password: string, role: string, firstName: string, lastName: string) => void;
  isLoading: boolean;
  currentUserRole: string;
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('user');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
      setError('Password must contain an uppercase letter, a lowercase letter, a number and a special character (@$!%*?&)');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    onCreate(email, password, role, firstName.trim(), lastName.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Create User</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">First Name</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full"
                  placeholder="Jane"
                />
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Last Name</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full"
                  placeholder="Doe"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Email</label>
            <div className="md-outlined-textfield">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                placeholder="user@example.com"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Password</label>
            <div className="md-outlined-textfield relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pr-10"
                placeholder="Minimum 12 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">
              At least 12 characters, including an uppercase letter, a lowercase letter, a number and a special character (@$!%*?&).
            </p>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Confirm Password</label>
            <div className="md-outlined-textfield relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pr-10"
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Role</label>
            <div className="md-outlined-textfield">
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full">
                <option value="user">User</option>
                {currentUserRole === 'super_admin' && (
                  <>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </>
                )}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({
  user,
  onClose,
  onUpdate,
  isLoading,
  currentUserRole
}: {
  user: User;
  onClose: () => void;
  onUpdate: (id: string, email: string, role: string, firstName: string, lastName: string) => void;
  isLoading: boolean;
  currentUserRole: string;
}) => {
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [role, setRole] = useState(user.role);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(user.id, email, role, firstName.trim(), lastName.trim());
  };

  const canEditRole = currentUserRole === 'super_admin' || user.role !== 'super_admin';
  const canEditOwnRole = user.id !== JSON.parse(localStorage.getItem('user') || '{}').id;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Edit User</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">First Name</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full"
                  placeholder="Jane"
                />
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Last Name</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full"
                  placeholder="Doe"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Email</label>
            <div className="md-outlined-textfield">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Role</label>
            <div className="md-outlined-textfield">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full"
                disabled={!canEditRole || !canEditOwnRole}
              >
                <option value="user">User</option>
                {currentUserRole === 'super_admin' && (
                  <option value="admin">Admin</option>
                )}
                {currentUserRole === 'super_admin' && canEditOwnRole && (
                  <option value="super_admin">Super Admin</option>
                )}
              </select>
            </div>
            {(!canEditRole || !canEditOwnRole) && (
              <p className="text-xs text-[#9CA3AF] mt-1.5">
                {!canEditRole ? 'Cannot edit super admin role' : 'Cannot edit your own role'}
              </p>
            )}
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Change Password Modal Component
const ChangePasswordModal = ({
  user,
  onClose,
  onPasswordChange,
  isLoading
}: {
  user: User;
  onClose: () => void;
  onPasswordChange: (id: string, password: string) => void;
  isLoading: boolean;
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
      setError('Password must contain an uppercase letter, a lowercase letter, a number and a special character (@$!%*?&)');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    onPasswordChange(user.id, password);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Change Password</h2>
        <p className="text-sm text-[#6B7280] mb-4">
          Change password for <strong className="text-[#374151]">{user.email}</strong>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">New Password</label>
            <div className="md-outlined-textfield relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pr-10"
                placeholder="Minimum 12 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1">
              At least 12 characters, including an uppercase letter, a lowercase letter, a number and a special character (@$!%*?&).
            </p>
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Confirm Password</label>
            <div className="md-outlined-textfield relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pr-10"
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
